import React, { createContext, useContext, useState, ReactNode } from "react";
import { Confirm } from "./Confirm";

interface ConfirmOptions {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
}

interface ConfirmContextType {
    showConfirm: (options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

interface ConfirmProviderProps {
    children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({});
    const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

    const showConfirm = (options: ConfirmOptions = {}): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmOptions({
                title: options.title || "Confirm",
                message: options.message || "Are you sure you want to leave?",
                confirmText: options.confirmText || "Leave",
                cancelText: options.cancelText || "Stay",
                ...options
            });
            setIsOpen(true);
            setResolvePromise(() => resolve);
        });
    };

    const handleConfirm = (confirmed: boolean) => {
        setIsOpen(false);
        if (resolvePromise) {
            resolvePromise(confirmed);
            setResolvePromise(null);
        }
    };

    const handleClose = () => {
        handleConfirm(false);
    };

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {children}
            <Confirm
                isOpen={isOpen}
                onConfirm={handleConfirm}
                onClose={handleClose}
                options={confirmOptions}
            />
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmContextType {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    }
    return context;
}